import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, Pencil, ClipboardList, Shield, Zap, Target } from "lucide-react";
import { roleLabel } from "../../lib/roleLists";
import { pickIcon } from "./pickIcons";

const RATING = {
  anchor: { dot: "#4ade80", label: "Anchor" },
  good: { dot: "#60a5fa", label: "Good" },
  limiter: { dot: "#f87171", label: "Fix now" },
};

// Compact skill chips: only rated skills show; unrated ones collapse into "+N not rated".
export function SkillChips({ skills, testid }) {
  const rated = skills.filter(s => RATING[s.rating]);
  const unrated = skills.length - rated.length;
  if (!skills.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
      {rated.map(s => (
        <span key={s.skillId} className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#1a1a1a] border border-white/5 text-[11px] text-[#e0e0e0]" title={RATING[s.rating].label} data-testid={`skill-tag-${s.skillId}`}>
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: RATING[s.rating].dot }} />{s.name}
        </span>
      ))}
      {unrated > 0 && <span className="text-[10px] text-[#575757]">+{unrated} not rated</span>}
    </div>
  );
}

function Panel({ icon: Icon, title, sub, count, children, testid }) {
  return (
    <div className="relative border border-white/10 bg-[#101010] p-4 min-h-[120px] overflow-hidden" data-testid={testid}>
      <div className="absolute inset-x-0 top-0 h-[2px] bg-[var(--org-accent)]/70" />
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-8 h-8 border border-[var(--org-accent)]/40 bg-[var(--org-accent)]/10 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[var(--org-accent)]" /></span>
        <div><div className="font-heading uppercase text-white text-lg md:text-xl leading-none">{title}</div>{sub && <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mt-0.5">{sub}</div>}</div>
        {count > 0 && <span className="ml-auto text-[10px] uppercase tracking-[0.3em] text-[#757575]">{count}</span>}
      </div>
      {children || <div className="text-xs text-[#575757]">—</div>}
    </div>
  );
}

// A pick with its icon; retired picks are dimmed and labelled.
function Pick({ id, fallback }) {
  const Icon = pickIcon(id, fallback);
  return (
    <li className="flex items-center gap-2.5 px-2.5 py-2 bg-[#161616] border border-white/5 text-sm text-[#e0e0e0] leading-snug" data-testid={`pick-${id}`}>
      <Icon className="w-4 h-4 text-[var(--org-accent)] shrink-0" />{roleLabel(id)}
    </li>
  );
}

// One team entry: slim header strip, role headline, four compact panels, then the archetype check and admin notes.
export function CoachEntryCard({ entry: e, admin, onPatch, onEdit }) {
  const [notes, setNotes] = useState(e.adminNotes || "");
  const c = e.comparisons || {}; const rva = c.roleVsArchetype;
  const isNew = admin && !e.reviewedAt;
  const past = e.status === "past";
  const allSkills = [...(c.responsibilitySkills || []), ...(c.matchups || []).flatMap(m => m.skills), ...(c.playTypes || []).flatMap(p => p.skills)].filter((s, i, a) => a.findIndex(x => x.skillId === s.skillId) === i);
  return (
    <div className={`border bg-[#141414] ${past ? "border-white/5 opacity-70" : "border-white/10"}`} data-testid={`coach-entry-${e.id}`}>
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5 bg-[#0f0f0f]">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0] truncate">
            <span className="text-white font-bold">{e.team}</span> · {e.season} · {e.coachName}
            {isNew && <span className="ml-2 px-1.5 py-0.5 text-[8px] bg-[var(--org-accent)] text-[#121212]" data-testid={`coach-entry-new-${e.id}`}>New</span>}
          </div>
          <div className="text-[10px] text-[#575757] truncate">{e.enteredBy === "coach" ? "Submitted by coach" : `Entered by ${e.enteredByName || "admin"}`} · {new Date(e.submittedAt).toLocaleDateString()}</div>
        </div>
        {admin && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => onPatch({ status: past ? "current" : "past", reviewed: true })} className={`px-2 py-1 text-[9px] uppercase tracking-[0.3em] border ${past ? "border-white/15 text-[#a0a0a0]" : "border-[#4ade80]/40 text-[#4ade80]"}`} data-testid={`coach-entry-status-${e.id}`}>{past ? "Past" : "Current"}</button>
            <button onClick={onEdit} className="px-2 py-1 text-[9px] uppercase tracking-[0.3em] border border-white/15 text-[#a0a0a0] hover:text-white flex items-center gap-1" data-testid={`coach-entry-edit-${e.id}`}><Pencil className="w-3 h-3" /> Edit</button>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Panel icon={ClipboardList} title="Responsibilities" count={(e.responsibilityIds || []).length} testid={`panel-resp-${e.id}`}>
            {(e.responsibilityIds || []).length > 0 && <ul className="space-y-1.5">{e.responsibilityIds.map(id => <Pick key={id} id={id} fallback={ClipboardList} />)}</ul>}
          </Panel>
          <Panel icon={Shield} title="Who they guard" count={(e.matchupIds || []).length} testid={`matchups-${e.id}`}>
            {(e.matchupIds || []).length > 0 && <ul className="space-y-1.5">{e.matchupIds.map(id => <Pick key={id} id={id} fallback={Shield} />)}</ul>}
          </Panel>
          <Panel icon={Zap} title="Play types" count={(e.playTypeIds || []).length} testid={`play-types-${e.id}`}>
            {(e.playTypeIds || []).length > 0 && <ul className="space-y-1.5">{e.playTypeIds.map(id => <Pick key={id} id={id} fallback={Zap} />)}</ul>}
          </Panel>
          <Panel icon={Target} title="Key skills" sub="vs latest assessment" testid={`key-skills-${e.id}`}>
            {allSkills.length > 0 && <>
              <SkillChips skills={allSkills} testid={`skill-chips-${e.id}`} />
              <div className="flex gap-3 mt-2 text-[9px] uppercase tracking-[0.2em] text-[#575757]">{Object.values(RATING).map(r => <span key={r.label} className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: r.dot }} />{r.label}</span>)}</div>
            </>}
          </Panel>
        </div>

        {(e.minutes || e.earnMoreMinutes || e.comments) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs border-t border-white/5 pt-3">
            {e.minutes && <div><div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mb-0.5">Minutes / game</div><div className="text-[#e0e0e0]">{e.minutes}</div></div>}
            {e.earnMoreMinutes && <div><div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mb-0.5">Would earn more minutes</div><div className="text-[#e0e0e0]">{e.earnMoreMinutes}</div></div>}
            {e.comments && <div className="sm:col-span-3"><div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mb-0.5">Comments</div><div className="text-[#e0e0e0]">{e.comments}</div></div>}
          </div>
        )}

        {rva && (
          <div className={`border-l-2 pl-3 py-1 text-[11px] ${rva.fits ? "border-[#4ade80]" : "border-[#f59e0b]"}`} data-testid={`role-vs-archetype-${e.id}`}>
            <div className={`flex items-center gap-1.5 font-bold uppercase tracking-[0.25em] text-[10px] ${rva.fits ? "text-[#4ade80]" : "text-[#f59e0b]"}`}>
              {rva.fits ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}{rva.fits ? "Role fits archetype" : "Role may not match archetype"}
            </div>
            <div className="mt-1 text-[#a0a0a0] space-y-0.5">
              <div><span className="text-[#757575]">Archetype:</span> {["offense", "defense"].filter(s => rva.primary?.[s]).map(s => `${s === "offense" ? "Off" : "Def"} ${rva.primary[s]}${rva.secondary?.[s] ? ` / ${rva.secondary[s]}` : ""}`).join(" · ")}</div>
              {rva.fits ? <div><span className="text-[#757575]">Shared skills:</span> {rva.overlap.join(", ")}</div> : <div><span className="text-[#757575]">Archetype skills:</span> {rva.archetypeSkills.join(", ")}</div>}
            </div>
          </div>
        )}

        {admin && (
          <textarea rows={2} value={notes} onChange={e2 => setNotes(e2.target.value)} onBlur={() => notes !== (e.adminNotes || "") && onPatch({ adminNotes: notes })} placeholder="My notes · admin only"
            className="w-full bg-[#101010] border border-white/5 text-sm text-white p-2 placeholder:text-[#575757] focus:outline-none focus:border-white/30" data-testid={`coach-entry-notes-${e.id}`} />
        )}
      </div>
    </div>
  );
}
